import axiosInstance from 'api/axiosInstance';

export interface VinVehicleInfo {
	make: string | null;
	model: string | null;
	modelYear: string | null;
	bodyType: string | null;
}

export interface ExtractVinResponse {
	vin: string;
	vehicleInfo: VinVehicleInfo | null;
}

// API response uses PascalCase
interface ExtractVinApiResponse {
	Vin?: string;
	VehicleInfo?: {
		Make: string | null;
		Model: string | null;
		ModelYear: string | null;
		BodyType: string | null;
	} | null;
	error?: string;
}

export class VinExtractionError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'VinExtractionError';
	}
}

/**
 * Extracts VIN from an image using OCR and decodes vehicle information.
 * @throws {VinExtractionError} When VIN cannot be extracted or API returns an error
 */
export const extractVin = async (
	base64Image: string,
): Promise<ExtractVinResponse> => {
	try {
		const API_URL = `/api2.0/tickets/extractVin`;
		const response = await axiosInstance.post<ExtractVinApiResponse>(
			API_URL,
			{
				imageData: base64Image,
			},
			{
				headers: {
					'Content-Type': 'application/json',
				},
			},
		);

		const data = response.data;

		// Check for error response (API returns 200 OK with error property)
		if (data.error) {
			throw new VinExtractionError(data.error);
		}

		// Validate that VIN is present in successful response
		if (!data.Vin) {
			throw new VinExtractionError('No VIN found in image');
		}

		// Transform PascalCase API response to camelCase for frontend
		const vehicleInfo: VinVehicleInfo | null = data.VehicleInfo
			? {
					make: data.VehicleInfo.Make,
					model: data.VehicleInfo.Model,
					modelYear: data.VehicleInfo.ModelYear,
					bodyType: data.VehicleInfo.BodyType,
				}
			: null;

		return {
			vin: data.Vin,
			vehicleInfo,
		};
	} catch (error) {
		// Re-throw VinExtractionError as-is (already has user-friendly message)
		if (error instanceof VinExtractionError) {
			throw error;
		}
		// Log and wrap other errors
		console.error('[extractVin] Failed to extract VIN:', error);
		throw error;
	}
};
